/**
 * The sport-keyed player-prop stat registry (nfl-player-props, 2026-09-03).
 *
 * `MlbPropStat` was the only prop stat type in the repo, and the whole prop
 * pipeline — `MlbPropStatGroup`, `buildPlayerPropSummaries`, the `|prop-`
 * synth key, the workbench adapter — was typed to it. NFL's per-game player
 * series share the same shape (yes-ladder rungs keyed game → stat → player →
 * threshold; hydracore stamps ONE `class: 'player-prop'` + `stat` vocabulary
 * for both sports), so the pipeline is widened to a union rather than cloned.
 *
 * The per-sport modules stay the authorities for their own ids, labels, order
 * and wording (`sports/mlb.ts`, `sports/nfl.ts`); this module only joins them
 * so a consumer can index by a `PropStat` without branching on sport at every
 * site. `MlbPropStat` is left in place — nothing is renamed — so the 24
 * existing importers keep compiling unchanged.
 *
 * Pure: no store, no fetch, no clock.
 */
import {
  MLB_PROP_STATS,
  MLB_PROP_STAT_LABELS,
  mlbPropRungLabels,
  type MlbPropStat,
} from '../kalshiTicker/sports/mlb';
import {
  NFL_PROP_BINARY_THRESHOLD,
  NFL_PROP_STATS,
  NFL_PROP_STAT_LABELS,
  nflPropRungLabel,
  nflPropRungLabels,
  type NflPropStat,
} from '../kalshiTicker/sports/nfl';

export type { MlbPropStat, NflPropStat };

/** Every player-prop stat id, both sports. The two vocabularies are disjoint
 *  (MLB `hits hr rbi tb sb ks outs hrr`; NFL `pass-yds … dst-td`), which is
 *  what lets a stat id stand alone in a synth key without a sport tag. */
export type PropStat = MlbPropStat | NflPropStat;

/** Sports with a live player-prop pipeline. */
export type PropSport = 'mlb' | 'nfl';

export const PROP_SPORTS: readonly PropSport[] = ['mlb', 'nfl'];

export const PROP_STATS_BY_SPORT: Record<PropSport, readonly PropStat[]> = {
  mlb: MLB_PROP_STATS,
  nfl: NFL_PROP_STATS,
};

/** Every stat id, MLB first then NFL — each sport in its own display order. */
export const PROP_STATS: readonly PropStat[] = [...MLB_PROP_STATS, ...NFL_PROP_STATS];

export const PROP_STAT_LABELS: Record<PropStat, string> = {
  ...MLB_PROP_STAT_LABELS,
  ...NFL_PROP_STAT_LABELS,
};

const MLB_SET: ReadonlySet<string> = new Set(MLB_PROP_STATS);
const NFL_SET: ReadonlySet<string> = new Set(NFL_PROP_STATS);

export function isPropStat(value: string): value is PropStat {
  return MLB_SET.has(value) || NFL_SET.has(value);
}

export function isMlbPropStat(value: string): value is MlbPropStat {
  return MLB_SET.has(value);
}

export function isNflPropStat(value: string): value is NflPropStat {
  return NFL_SET.has(value);
}

export function propStatSport(stat: PropStat): PropSport {
  return MLB_SET.has(stat) ? 'mlb' : 'nfl';
}

/** Position of a stat within ITS SPORT's display order — the category order
 *  the day-wide slate model sorts player groups by. */
export function propStatOrder(stat: PropStat): number {
  return PROP_STATS_BY_SPORT[propStatSport(stat)].indexOf(stat);
}

/** A stat whose markets are structured yes/no per player rather than a
 *  threshold ladder (the NFL touchdown families). Such a stat carries a FIXED
 *  rung threshold as identity — see `NFL_PROP_BINARY_THRESHOLD` — so the
 *  numeric `PropRung` / synth-key contract holds without a null branch. */
export function propBinaryThreshold(stat: PropStat): number | null {
  return isNflPropStat(stat) ? NFL_PROP_BINARY_THRESHOLD[stat] ?? null : null;
}

export function isBinaryPropStat(stat: PropStat): boolean {
  return propBinaryThreshold(stat) !== null;
}

/** The rung without the player — "6+ Strikeouts", "350+ Passing Yards", or
 *  the family name for a touchdown binary ("1st Touchdown"). */
export function propRungLabel(stat: PropStat, threshold: number): string {
  return isMlbPropStat(stat)
    ? `${threshold}+ ${MLB_PROP_STAT_LABELS[stat]}`
    : nflPropRungLabel(stat, threshold);
}

/** The ladder's threshold tag — "6+" on a ladder, "Yes/No" on a binary. */
export function propRungTag(stat: PropStat, threshold: number): string {
  return isBinaryPropStat(stat) ? 'Yes/No' : `${threshold}+`;
}

/** The two sides of ONE prop rung (#251): YES names the outcome, NO is its
 *  complement, both worded from the same parts by the sport's own authority.
 *  `playerKey` lets the NFL wording recognise the reserved `NONE` leg. */
export function propRungLabels(
  playerName: string,
  teamCode: string | null | undefined,
  stat: PropStat,
  threshold: number,
  playerKey?: string,
): { label: string; notLabel: string } {
  return isMlbPropStat(stat)
    ? mlbPropRungLabels(playerName, teamCode, stat, threshold)
    : nflPropRungLabels(playerName, teamCode, stat, threshold, playerKey);
}
